import { useMemo, useState } from "react";
import type { JobStatus } from "@clevis/shared-types";
import { cn } from "@clevis/shared-ui";

// A month calendar of jobs, for one job's visits, a project, or a customer.
// Grid on wide screens, a day-by-day list on phones. Tap a job to open it.

export interface CalendarJob {
  id: string;
  title: string;
  startAt: number | null;
  status: JobStatus;
  lot?: string;
  sub?: string;
}

const STATUS_CHIP: Record<JobStatus, string> = {
  scheduled: "bg-steel/15 text-ink",
  in_progress: "bg-brandorange/20 text-brandorange-dark",
  done: "bg-success/15 text-success",
  canceled: "bg-steel/10 text-steel line-through",
};
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const dayKey = (ms: number) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};
const time = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

function startMonth(jobs: CalendarJob[]): number {
  const now = new Date();
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const dated = jobs.filter((j) => j.startAt).map((j) => j.startAt!);
  if (dated.length === 0) return thisMonth;
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime();
  if (dated.some((t) => t >= thisMonth && t < nextMonth)) return thisMonth;
  // Nothing this month: the next upcoming job's month, else the latest one's.
  const upcoming = dated.filter((t) => t >= nextMonth).sort((a, b) => a - b)[0];
  const pick = upcoming ?? Math.max(...dated);
  const d = new Date(pick);
  return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
}

export function JobsCalendar({ jobs, onOpen }: { jobs: CalendarJob[]; onOpen: (jobId: string) => void }) {
  const [month, setMonth] = useState(() => startMonth(jobs));
  const [openDay, setOpenDay] = useState<string | null>(null);
  const m = new Date(month);
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarJob[]>();
    for (const j of jobs) {
      if (!j.startAt) continue;
      const k = dayKey(j.startAt);
      (map.get(k) ?? map.set(k, []).get(k)!).push(j);
    }
    for (const list of map.values()) list.sort((a, b) => (a.startAt ?? 0) - (b.startAt ?? 0));
    return map;
  }, [jobs]);

  const first = new Date(m.getFullYear(), m.getMonth(), 1);
  const gridStart = new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay());
  const days = Array.from({ length: 42 }, (_, i) => new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
  const inMonth = days.filter((d) => d.getMonth() === m.getMonth());
  const monthCount = inMonth.reduce((s, d) => s + (byDay.get(dayKey(d.getTime()))?.length ?? 0), 0);
  const undated = jobs.filter((j) => !j.startAt).length;
  const todayKey = dayKey(Date.now());
  const shift = (n: number) => {
    setMonth(new Date(m.getFullYear(), m.getMonth() + n, 1).getTime());
    setOpenDay(null);
  };

  const chip = (j: CalendarJob) => (
    <button
      key={j.id}
      className={cn("block w-full truncate rounded px-1 py-0.5 text-left text-[11px]", STATUS_CHIP[j.status])}
      title={`${j.lot ? `${j.lot} · ` : ""}${j.title}`}
      onClick={() => onOpen(j.id)}
    >
      {j.lot ? <strong>{j.lot} </strong> : null}
      {j.title}
    </button>
  );

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button className="rounded px-2 py-0.5 text-sm text-steel hover:bg-cream hover:text-ink" onClick={() => shift(-1)} aria-label="Previous month">
            ‹
          </button>
          <span className="min-w-36 text-center text-sm font-semibold text-ink">
            {m.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
          </span>
          <button className="rounded px-2 py-0.5 text-sm text-steel hover:bg-cream hover:text-ink" onClick={() => shift(1)} aria-label="Next month">
            ›
          </button>
          <button
            className="ml-1 text-xs text-steel underline hover:text-ink"
            onClick={() => {
              const d = new Date();
              setMonth(new Date(d.getFullYear(), d.getMonth(), 1).getTime());
            }}
          >
            Today
          </button>
        </div>
        <span className="text-[11px] text-steel">
          {monthCount} this month{undated ? ` · ${undated} with no date` : ""}
        </span>
      </div>

      {/* Wide screens: the month grid */}
      <div className="hidden sm:block">
        <div className="grid grid-cols-7 border-l border-t border-steel/15 text-[11px]">
          {WEEKDAYS.map((w) => (
            <div key={w} className="border-b border-r border-steel/15 bg-cream/60 px-1 py-0.5 font-semibold text-steel">
              {w}
            </div>
          ))}
          {days.map((d) => {
            const k = dayKey(d.getTime());
            const list = byDay.get(k) ?? [];
            const outside = d.getMonth() !== m.getMonth();
            const expanded = openDay === k;
            return (
              <div key={k} className={cn("min-h-20 border-b border-r border-steel/15 p-0.5", outside && "bg-cream/30")}>
                <div className={cn("px-0.5 text-[11px]", k === todayKey ? "font-bold text-brandorange-dark" : outside ? "text-steel/50" : "text-steel")}>
                  {d.getDate()}
                </div>
                <div className="space-y-0.5">
                  {(expanded ? list : list.slice(0, 3)).map(chip)}
                  {list.length > 3 && !expanded ? (
                    <button className="px-1 text-[11px] text-steel underline hover:text-ink" onClick={() => setOpenDay(k)}>
                      +{list.length - 3} more
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Phones: the month's days that have jobs */}
      <div className="space-y-2 sm:hidden">
        {inMonth
          .filter((d) => byDay.has(dayKey(d.getTime())))
          .map((d) => (
            <div key={d.toDateString()}>
              <div className={cn("text-xs font-semibold", dayKey(d.getTime()) === todayKey ? "text-brandorange-dark" : "text-ink")}>
                {d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
              </div>
              <div className="mt-0.5 space-y-0.5">
                {byDay.get(dayKey(d.getTime()))!.map((j) => (
                  <button
                    key={j.id}
                    className={cn("flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs", STATUS_CHIP[j.status])}
                    onClick={() => onOpen(j.id)}
                  >
                    <span className="min-w-0 truncate">
                      {j.lot ? <strong>{j.lot} · </strong> : null}
                      {j.title}
                    </span>
                    <span className="shrink-0 text-[11px] opacity-70">{time(j.startAt!)}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        {monthCount === 0 ? <p className="text-xs text-steel">No jobs this month.</p> : null}
      </div>
    </div>
  );
}
